/**
 * /erp/products/new — create a new product (Manager+ via /erp shell).
 *
 * The form auto-generates the code (PRD-NNNNN) but lets the manager
 * override at create time. ``linked_raw_material_id`` lets the manager
 * tag the product as an 'intermediate product' — auto-mirroring the
 * name / UoM / is_active onto a sibling raw material row from then on.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ApiError, type ErpProductCreate, erpApi, isErpCatalogConflict } from "../lib/api";
import { ERP_QUERY_KEYS, groupUomsByCategory, useRawMaterials, useUoms } from "../lib/erp";
import { toast } from "../lib/toast";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/erp/products/new")({
  component: NewProductPage,
});

function NewProductPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uomsQ = useUoms();
  const rawsQ = useRawMaterials();
  const isProduction = useAuthStore((state) => state.user?.role === "production");

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [uom, setUom] = useState("");
  const [defaultPrice, setDefaultPrice] = useState("");
  const [linkedRawId, setLinkedRawId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (body: ErpProductCreate) => erpApi.products.create(body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.products });
      // Mirror also affects raw-materials list — invalidate.
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.rawMaterials });
      toast.success(`Product ${created.code} created.`);
      navigate({ to: "/erp/products/$productId", params: { productId: created.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && isErpCatalogConflict(err.detail)) {
        setError(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not create product.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!uom) {
      setError("Pick a unit of measure.");
      return;
    }
    setError(null);
    const body: ErpProductCreate = {
      name: name.trim(),
      uom,
    };
    if (code.trim()) body.code = code.trim();
    if (category.trim()) body.category = category.trim();
    if (!isProduction && defaultPrice.trim()) body.default_sell_price = defaultPrice.trim();
    if (!isProduction && linkedRawId) body.linked_raw_material_id = linkedRawId;
    if (!isProduction && notes.trim()) body.notes = notes.trim();
    createMutation.mutate(body);
  };

  const groupedUoms = groupUomsByCategory(uomsQ.data?.items ?? []);
  const availableRaws = (rawsQ.data?.items ?? []).filter((r) => r.is_active);

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-4 sm:p-6">
      <header className="space-y-2">
        <Link
          to="/erp/products"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
        >
          <ArrowLeft className="size-3" aria-hidden />
          Back to products
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">New product</h1>
      </header>

      <form onSubmit={onSubmit} className="space-y-4 rounded-xl border border-[var(--border)] p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Code" hint="Optional — auto-generates as PRD-NNNNN.">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={32}
              placeholder="auto"
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 font-mono text-sm outline-none focus:border-[var(--accent)]"
            />
          </Field>
          <Field label="Name" required>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            />
          </Field>
          <Field label="Category" hint="Optional — for example, Handmade Sinks.">
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              maxLength={100}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            />
          </Field>
          <Field label="Unit of measure" required>
            <select
              value={uom}
              onChange={(e) => setUom(e.target.value)}
              required
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="" disabled>
                Pick a UoM…
              </option>
              {groupedUoms.map((g) => (
                <optgroup key={g.category} label={g.category}>
                  {g.items.map((i) => (
                    <option key={i.code} value={i.code}>
                      {i.code} — {i.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          {!isProduction && (
          <Field label="Default sell price" hint="Optional — auto-fills delivery lines.">
            <input
              type="number"
              step="0.01"
              min="0"
              value={defaultPrice}
              onChange={(e) => setDefaultPrice(e.target.value)}
              placeholder="0.00"
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            />
          </Field>
          )}
        </div>
        {!isProduction && (
          <>

        <Field
          label="Linked raw material"
          hint="Optional — set if this product is also consumed as a raw input during manufacturing. Name / UoM / active state will mirror onto the linked raw."
        >
          <select
            value={linkedRawId}
            onChange={(e) => setLinkedRawId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          >
            <option value="">— Not linked —</option>
            {availableRaws.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} — {r.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            rows={3}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>
          </>
        )}

        {error && (
          <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Link
            to="/erp/products"
            className="rounded-md border border-[var(--border)] px-3 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {createMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            Create
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: input is rendered via {children}
    <label className="block space-y-1">
      <span className="text-xs font-medium opacity-80">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] opacity-60">{hint}</span>}
    </label>
  );
}
